import { useMutation, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router';
import { AxiosError, AxiosResponse } from 'axios';
import { createCodePanelTemplate, CreatePanelTemplateRequest } from './api';
import { BUILT_IN_CHART_TYPES } from '@/experiment-management-shared/constants';
import { getURLQueryParams } from '@shared/utils/url';
import { useDispatch, useSelector } from 'react-redux';
import dashboardChartsActions from '@/actions/dashboardChartsActions';
import { useActiveWorkspace } from '@shared/hooks';
import { generatePythonPanelCodeVersion } from '@experiment-management-shared';
import { createLibraryTemplateInstance } from '@/experiment-management-shared/helpers';
import { getSelectedProjectId } from '@/reducers/ui/projectsUiReducer';
import { TemplateRevision } from '@/experiment-management-shared/api/library-panel/types';
import alertsUtil from '@/util/alertsUtil';
import { snackbarTypes } from '@/constants/alertTypes';
import { SELECT_PANEL_PATH } from '@/experiment-management-shared/constants/visualizationConstants';

interface CustomErrorResponse {
  msg: string;
}

interface CreateCodeTemplateError extends AxiosError {
  response?: AxiosResponse<CustomErrorResponse>;
}

export default function useCreateCodeTemplate({
  onClose,
  onEditCustomChart,
  resetInitialChartState
}: {
  onClose: () => void;
  onEditCustomChart: (chartId: string, libraryTemplateInstance: object) => void;
  resetInitialChartState: () => void;
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const activeWorkspace = useActiveWorkspace();
  const { viewId } = useParams<{ viewId: string }>();
  const projectId = useSelector(getSelectedProjectId);
  const mutation = useMutation<
    TemplateRevision,
    CreateCodeTemplateError,
    CreatePanelTemplateRequest
  >(
    async payload => {
      return createCodePanelTemplate(payload);
    },
    {
      onSuccess: (response, variables) => {
        const { templateId, revisionId } = response;
        const pyCode = variables.code.pyCode;

        const queryParams = getURLQueryParams(history.location.search);

        const chartId = queryParams.get('chartId');

        dispatch(
          alertsUtil.openSnackbarDialog(
            snackbarTypes.SUCCESS_API_RESPONSE,
            `You successfully saved '${variables.templateName}' to the panel library`
          )
        );
        // if the user saved template from an existing chart, we need to update the chart with the new template
        if (chartId) {
          const libraryTemplateInstance = createLibraryTemplateInstance({
            instanceName: variables.templateName,
            templateRevisionId: revisionId,
            projectId,
            templateId,
            viewId
          });

          onEditCustomChart(chartId, libraryTemplateInstance);
        }

        const chartIdString = chartId ? `&chartId=${chartId}` : '';
        const hash = `#${SELECT_PANEL_PATH}&chartType=${BUILT_IN_CHART_TYPES.libraryPanel}&templateId=${templateId}${chartIdString}`;

        history.replace({
          ...history.location,
          hash
        });

        dispatch(
          dashboardChartsActions.updateChartForm(
            BUILT_IN_CHART_TYPES.libraryPanel,
            {
              chartName: variables.templateName,
              instanceName: variables.templateName,
              description: variables.code.description,
              codeVersion: generatePythonPanelCodeVersion(),
              code: pyCode
            }
          )
        );

        queryClient.invalidateQueries([
          'panelTemplates',
          { teamId: activeWorkspace?.id, scope: 'WORKSPACE' }
        ]);

        queryClient.invalidateQueries([
          'panelTemplatesCount',
          { teamId: activeWorkspace?.id }
        ]);

        resetInitialChartState();
        onClose();
      }
    }
  );

  return mutation;
}
